// packages/api/src/sessionRefresh.ts
//
// ROADMAP #239 (owner, 2026-10-28: "make the token lifetime short (15 min) and do refreshing when needed, but do not sign
// out user until session expired … If token expired silently try to refresh and repeat the API call").
//
// ONE refresh at a time, for every request that met an expired session:
//   • in a tab — every request refused meanwhile waits on the SAME refresh (single-flight), then repeats once;
//   • across tabs — they share ONE refresh cookie, and a refresh ROTATES it (ms_auth removes the old session row), so two
//     tabs refreshing at once would sign the second out. A `navigator.locks` lock runs them one after the other, and a
//     tab that waited finds the renewal another tab just made (`REFRESHED_AT_KEY`, newer than its own request) and simply
//     repeats its call with the cookie already renewed — no second rotation.
// ⚠ The refresh itself is supplied by the app (`setSessionRefresher`): this package does not know the mutation, and a site
//   with none (the public event site) keeps today's behaviour exactly — a refusal goes straight to the auth handler.
// ⚠ What signs a user out is unchanged: only a FAILED refresh goes on to the handler (`me()`, then sign-out — App.tsx).
// ⚠⚠ RENEW BEFORE SENDING, NOT ONLY AFTER A REFUSAL (review 2026-10-30). The actions that read a token only if there is one
//   (`tryAuth` — 49 in eventsmagic-api: the guest list, an event, the dashboard cards, an upload) answer an EXPIRED token
//   as ANONYMOUS — an empty list, "event not found" — never a refusal to refresh on. So a call whose token is known to be
//   (nearly) expired renews first (`sessionNeedsRenewal`); the refusal path stays for what that cannot see.

/** Asks the server for a new access token; true when the session was renewed. Must not sign anyone out itself. */
export type SessionRefresher = () => Promise<boolean>;

/** When this browser last renewed its session (ms since epoch), shared by every tab. */
export const REFRESHED_AT_KEY = "em_session_refreshed_at";

/** When the current access token expires (ms since epoch), as the server said on sign-in / refresh. */
export const TOKEN_EXPIRES_AT_KEY = "em_session_token_expires_at";

/** The cross-tab lock's name. */
export const REFRESH_LOCK_NAME = "em-session-refresh";

/** Renew this long BEFORE the token's expiry (the request still has to reach the server in time). */
export const SESSION_RENEW_AHEAD_MS = 60_000;

/**
 * The longest a tab waits for ANOTHER tab's refresh lock. A tab frozen mid-refresh (a suspended background tab on a phone)
 * would otherwise hold every other tab's calls for good; past this, the waiting tab renews without the lock (at worst one
 * extra rotation, and the frozen tab's own refresh then fails harmlessly — its next call uses the new cookie).
 */
export const REFRESH_LOCK_WAIT_MS = 10_000;

let refresher: SessionRefresher | null = null;
let inflight: Promise<boolean> | null = null;

/** Register how this app renews its session (App start-up). */
export const setSessionRefresher = (fn: SessionRefresher): void => {
  refresher = fn;
};

/** Forget it (sign-out, tests). Also drops a refresh in flight from this tab's view. */
export const clearSessionRefresher = (): void => {
  refresher = null;
  inflight = null;
};

/** Whether a refresh can be tried at all. */
export const hasSessionRefresher = (): boolean => refresher !== null;

const readNumber = (key: string): number => {
  try {
    const v = Number(globalThis.localStorage?.getItem(key) ?? 0);

    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
};

const writeNumber = (key: string, value: number | null): void => {
  try {
    if (value == null) {
      globalThis.localStorage?.removeItem(key);
    } else {
      globalThis.localStorage?.setItem(key, String(value));
    }
  } catch {
    // no storage (a private window) — the lock alone still orders the refreshes
  }
};

/** Record a renewal made here — sign-in counts as one — and when its access token expires (null: unknown). */
export const noteSessionRenewed = (tokenExpiresAt: number | null, now: number = Date.now()): void => {
  writeNumber(REFRESHED_AT_KEY, now);
  writeNumber(TOKEN_EXPIRES_AT_KEY, tokenExpiresAt != null && Number.isFinite(tokenExpiresAt) ? tokenExpiresAt : null);
};

/** Forget both (sign-out). */
export const forgetSessionRenewal = (): void => {
  writeNumber(REFRESHED_AT_KEY, null);
  writeNumber(TOKEN_EXPIRES_AT_KEY, null);
};

/** When the current access token expires, or null when not known. */
export const sessionTokenExpiresAt = (): number | null => {
  const v = readNumber(TOKEN_EXPIRES_AT_KEY);

  return v > 0 ? v : null;
};

/** Is the token KNOWN to be expired, or within `SESSION_RENEW_AHEAD_MS` of it? (Unknown → false: nothing to go on.) */
export const sessionNeedsRenewal = (now: number = Date.now()): boolean => {
  const expiresAt = sessionTokenExpiresAt();

  return expiresAt != null && expiresAt - now < SESSION_RENEW_AHEAD_MS;
};

type LockManagerLike = { request: <T>(name: string, options: { signal?: AbortSignal }, cb: () => Promise<T>) => Promise<T> };

const lockManager = (): LockManagerLike | null => {
  const locks = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator?.locks;

  return locks && typeof locks.request === "function" ? locks : null;
};

/**
 * Renew the session for a request SENT at `sentAt` that met an expired one. Resolves true when the call can be repeated:
 * this tab renewed it, or another tab did after that request went out. False: no refresher, or the renewal failed.
 * Never throws.
 */
export const refreshSessionOnce = (sentAt: number): Promise<boolean> => {
  const fn = refresher;

  if (!fn) {
    return Promise.resolve(false);
  }

  if (inflight) {
    return inflight;
  }

  const run = async (): Promise<boolean> => {
    // Another tab (or an earlier refresh here) renewed it after this request went out: its cookie is the new one already.
    if (readNumber(REFRESHED_AT_KEY) > sentAt) {
      return true;
    }

    try {
      return (await fn()) === true;
    } catch {
      return false;
    }
  };

  inflight = underRefreshLock(run)
    .catch(() => false)
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/**
 * Run `fn` holding the cross-tab refresh lock — or without it when there is none, or when another tab has held it longer
 * than `REFRESH_LOCK_WAIT_MS` (see there).
 */
const underRefreshLock = async <T,>(fn: () => Promise<T>): Promise<T> => {
  const locks = lockManager();
  const timeout = (AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal }).timeout;

  if (!locks) {
    return fn();
  }

  try {
    return await locks.request(REFRESH_LOCK_NAME, timeout ? { signal: timeout(REFRESH_LOCK_WAIT_MS) } : {}, fn);
  } catch (error) {
    // Only a lock we never got (the wait ran out) falls back; a failure INSIDE `fn` is its own answer.
    if ((error as { name?: string })?.name === "AbortError" || (error as { name?: string })?.name === "TimeoutError") {
      return fn();
    }

    throw error;
  }
};

/**
 * Sign-out (and anything else that ends the session) runs AFTER a refresh in flight and under the same cross-tab lock — or
 * a refresh answer landing after it would hand the browser a NEW session (cookies, the stored user) the sign-out never
 * ended: on a shared computer, still signed in.
 */
export const withSessionRefreshSettled = async <T,>(fn: () => Promise<T>): Promise<T> => {
  if (inflight) {
    await inflight;
  }

  return underRefreshLock(fn);
};
